'use strict'

import {createScapiFetch} from './utils'

/**
 * Asynchronously retrieves the costs of shipping methods for a specified site and locale.
 *
 * @param {string} accessToken - The access token used for authentication in the API request.
 * @param {string} siteId - The identifier for the site for which shipping methods costs are being fetched.
 * @param {string} locale - The locale identifier to specify the regional context for the shipping methods.
 * @returns {Promise<Object>} A promise that resolves to an object containing the shipping methods costs, or an empty object if an error occurs.
 *
 * @throws Will log an error message to the console if the API request fails.
 */
class BasketMgr {
    constructor(config) {
        this.config = config
        this.fetch = createScapiFetch(config)
    }

    /**
     * Asynchronously retrieves the costs of shipping methods for a specified site and locale.
     *
     * @param {string} accessToken - The access token used for authentication in the API request.
     * @param {string} siteId - The identifier for the site for which shipping methods costs are being fetched.
     * @param {string} locale - The locale identifier to specify the regional context for the shipping methods.
     * @returns {Promise<Object>} A promise that resolves to an object containing the shipping methods costs, or an empty object if an error occurs.
     *
     * @throws Will log an error message to the console if the API request fails.
     */
    async getShippingMethodsCosts(accessToken, siteId, locale) {
        const urlParam = {
            querystring: {}
        }

        const endpoint = 'getShippingMethodsCosts'
        const method = 'GET'
        const args = [urlParam]
        const methodName = 'getShippingMethodsCosts'
        const requestParams = {
            apiPath: 'basket',
            accessToken: accessToken,
            isCached: false,
            siteID: siteId,
            localeID: locale
        }
        const apiVersion = 'v1'

        try {
            const getShippingMethodsCosts = await this.fetch(
                endpoint,
                method,
                args,
                methodName,
                requestParams,
                apiVersion
            )

            return getShippingMethodsCosts || {}
        } catch (error) {
            console.error('Error fetching shipping methods costs:', error)
            return {}
        }
    }

    async getPdpDeliveryEstimations(accessToken, siteId, locale, countryCode) {
        const urlParam = {
            querystring: {
                c_country: countryCode
            }
        }

        const endpoint = 'getPdpDeliveryEstimations'
        const method = 'GET'
        const args = [urlParam]
        const methodName = 'getPdpDeliveryEstimations'
        const requestParams = {
            apiPath: 'basket',
            accessToken: accessToken,
            isCached: false,
            siteID: siteId,
            localeID: locale
        }
        const apiVersion = 'v1'

        try {
            const estimations = await this.fetch(
                endpoint,
                method,
                args,
                methodName,
                requestParams,
                apiVersion
            )

            return estimations || {}
        } catch (error) {
            console.error('Error fetching PDP delivery estimations:', error)
            return {}
        }
    }

    /**
     * Asynchronously assigns Ship-from-Store (SFS) configurations to a basket.
     *
     * This function sends a POST request to the 'sfsBasketAssignments' endpoint to
     * assign SFS configurations to the current basket based on the provided site ID
     * and locale. It utilizes an access token for authentication and specifies the
     * API version to use.
     *
     * @async
     * @param {string} accessToken - The access token for authenticating the API request.
     * @param {string} siteId - The identifier for the site to which the basket belongs.
     * @param {string} locale - The locale identifier for the request, affecting language and regional settings.
     * @param {boolean} [isCartPage=false] - Indicates if the request is made from the cart page (affects assignment logic).
     * @param {string} [shipToCountry] - Explicit ship-to country to use for SFS assignment.
     * @param {boolean} [resetAssignments=false] - Whether existing assignments must be reset before reassignment.
     * @returns {Promise<Object>} A promise that resolves to the SFS basket assignments object, or an empty object if an error occurs.
     * @throws Will log an error message to the console if the API request fails.
     */
    async sfsBasketAssignments(
        accessToken,
        siteId,
        locale,
        isCartPage = false,
        shipToCountry,
        resetAssignments = false
    ) {
        const urlParam = {
            querystring: {
                c_isCartPage: isCartPage,
                c_country: shipToCountry,
                c_resetAssignments: resetAssignments
            }
        }

        const endpoint = 'sfsBasketAssignments'
        const method = 'POST'
        const args = [urlParam]
        const methodName = 'sfsBasketAssignments'
        const requestParams = {
            apiPath: 'basket',
            accessToken: accessToken,
            isCached: false,
            siteID: siteId,
            localeID: locale
        }
        const apiVersion = 'v1'

        try {
            const sfsBasketAssignments = await this.fetch(
                endpoint,
                method,
                args,
                methodName,
                requestParams,
                apiVersion
            )

            return sfsBasketAssignments || {}
        } catch (error) {
            console.error('Error on assignment of SFS on basket:', error)
            return {}
        }
    }

    /**
     * Asynchronously updates a line item in the basket using the SFS (Ship-from-Store) API.
     *
     * @param {string} accessToken - The access token for authenticating the API request.
     * @param {string} siteId - The identifier for the site where the basket is located.
     * @param {string} locale - The locale identifier for the request.
     * @param {string} shipToCountry - The country code to which the item is being shipped.
     * @param {Object} bodyRequest - The request body containing the line item details to be updated.
     * @returns {Promise<Object>} A promise that resolves to the response from the SFS API, or an empty object if an error occurs.
     *
     * @throws Will log an error message to the console if the API request fails.
     */
    async sfsUpdateLineItem(accessToken, siteId, locale, shipToCountry, bodyRequest) {
        const urlParam = {
            querystring: {}
        }

        const endpoint = 'sfsUpdateLineItem'
        const method = 'POST'
        const args = [urlParam]
        const methodName = 'sfsUpdateLineItem'
        const requestParams = {
            apiPath: 'basket',
            accessToken: accessToken,
            isCached: false,
            siteID: siteId,
            localeID: locale,
            countryCode: shipToCountry,
            body: bodyRequest
        }
        const apiVersion = 'v1'

        try {
            const sfsUpdateLineItem = await this.fetch(
                endpoint,
                method,
                args,
                methodName,
                requestParams,
                apiVersion
            )

            return sfsUpdateLineItem || {}
        } catch (error) {
            console.error('Error on assignment of SFS on basket:', error)
            return {}
        }
    }

    /**
     * Asynchronously checks the inventory availability for the current basket.
     *
     * This function sends a POST request to the 'checkBasketInventoryAvailability' endpoint
     * to verify if the items in the basket are available in the inventory. It utilizes
     * the provided access token, site ID, and locale to perform the check.
     *
     * @async
     * @param {string} accessToken - The access token for authenticating the API request.
     * @param {string} siteId - The identifier for the site where the basket is located.
     * @param {string} locale - The locale ID to be used for the request.
     * @returns {Promise<Object>} A promise that resolves to an object indicating the availability
     *                            of the basket items. The object contains a boolean property 'available'.
     *                            If an error occurs, an empty object is returned.
     * @throws Will log an error message to the console if the request fails.
     */
    async checkBasketInventoryAvailability(accessToken, siteId, locale) {
        const urlParam = {
            querystring: {}
        }

        const endpoint = 'checkBasketInventoryAvailability'
        const method = 'POST'
        const args = [urlParam]
        const methodName = 'checkBasketInventoryAvailability'
        const requestParams = {
            apiPath: 'basket',
            accessToken: accessToken,
            isCached: false,
            siteID: siteId,
            localeID: locale
        }
        const apiVersion = 'v1'
        try {
            const checkBasketInventoryAvailability = await this.fetch(
                endpoint,
                method,
                args,
                methodName,
                requestParams,
                apiVersion
            )

            return checkBasketInventoryAvailability || {available: false}
        } catch (error) {
            console.error('Error on check inventory of basket:', error)
            return {}
        }
    }

    /**
     * Updates the locale for all product line items in the current basket by making an API call.
     *
     * @async
     * @param {string} accessToken - The access token used for API authentication.
     * @param {string} siteId - The identifier for the site whose basket is being updated.
     * @param {string} locale - The locale to set for the product line items.
     * @returns {Promise<Object>} - A promise that resolves to the API response object indicating the update status, or an empty object if an error occurs.
     */
    async updateProductLineItemsLocale(accessToken, siteId, locale) {
        const urlParam = {
            querystring: {}
        }

        const endpoint = 'updateProductLineItemsLocale'
        const method = 'POST'
        const args = [urlParam]
        const methodName = 'updateProductLineItemsLocale'
        const requestParams = {
            apiPath: 'basket',
            accessToken: accessToken,
            isCached: false,
            siteID: siteId,
            localeID: locale
        }
        const apiVersion = 'v1'
        try {
            const updateProductLineItemsLocale = await this.fetch(
                endpoint,
                method,
                args,
                methodName,
                requestParams,
                apiVersion
            )

            return updateProductLineItemsLocale || {updated: false}
        } catch (error) {
            console.error('Error on updating basket locale:', error)
            return {}
        }
    }
}

export default BasketMgr
